import React, { useCallback, useState, useMemo, useEffect } from 'react';
import Joi from 'joi';

import { useForgotPasswordMutation } from 'api/main';
import { EMAIL_VALIDATION_OPTIONS } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { CustomTextField, SubmitButton, SuccessMessage } from 'components';
import TunnelStep from 'containers/tunnel/common/TunnelStep';
import TunnelBox from 'containers/tunnel/common/TunnelBox';
import { Typography, Box } from '@mui/material';
import { validateSchema } from 'utils/helpers/schemas';

const emailNotFoundError = (
    <>
        <span style={{ display: 'block' }}>{texts.EMAIL_ADRESSE_NEST_ASSOICEE}</span>
        <span style={{ display: 'block' }}>{texts.VEUILLEZ_RENSEIGNER}</span>
    </>
);

const emailNotVerifiedError = (
    <>
        <span style={{ display: 'block' }}>{texts.EMAIL_ADDRESS_NOT_VERIFIED}</span>
        <span style={{ display: 'block' }}>{texts.VEUILLEZ_RENSEIGNER}</span>
    </>
);

const ForgotPassword = () => {
    const [forgotPassword, { isSuccess, isLoading: buttonLoader, error, isError }] =
        useForgotPasswordMutation();

    const [email, setEmail] = useState('');
    const [formError, setFormError] = useState('');

    const schema = useMemo(
        () => ({
            email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
                'string.empty': texts.SAISIR_VOTRE_ADRESSE_EMAIL,
                'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
            }),
        }),
        []
    );

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, { email });
        setFormError(newErrors.email);

        return valid;
    }, [email, schema]);

    const handleChange = useCallback(({ target: { value } }) => setEmail(value.trim()), []);

    const handleBlur = useCallback(
        e => {
            let { error } = schema.email.validate(e.target.value);
            setFormError(error ? error.details[0].message : '');
        },
        [schema]
    );

    const handleReset = useCallback(() => {
        if (formIsValid()) {
            forgotPassword(email);
        }
    }, [formIsValid, forgotPassword, email]);

    const handleSubmit = useCallback(
        e => {
            e.preventDefault();
            handleReset();
        },
        [handleReset]
    );

    useEffect(() => {
        if (isError) {
            if (error === 'not_found') {
                setFormError(emailNotFoundError);
            }
            if (error === 'not_verified') {
                setFormError(emailNotVerifiedError);
            }
        }
    }, [isError, error]);

    return (
        <Box width="100%" pt="90px">
            <TunnelStep title={texts.REINITIALISATION_DE_VOTRE_DE_PASSE}>
                <TunnelBox>
                    <Typography variant="subtitle1" display="block">
                        {texts.SAISIR_VOTRE_ADRESSE_EMAIL}
                    </Typography>
                    <Typography gutterBottom variant="subtitle1" display="block">
                        {texts.UN_LIEN_ENVOYE}
                    </Typography>
                    <Box
                        onSubmit={handleSubmit}
                        sx={{
                            width: '100%',
                            marginTop: '30px',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                        component="form"
                    >
                        <CustomTextField
                            inputProps={{
                                autoCapitalize: 'off',
                                autoComplete: 'off',
                                spellCheck: 'off',
                                autoCorrect: 'off',
                            }}
                            formControlProps={{
                                sx: theme => ({
                                    flexBasis: '100%',
                                    [theme.breakpoints.up('sm')]: {
                                        flexBasis: '70%',
                                    },
                                }),
                            }}
                            label={texts.ADRESSE_EMAIL}
                            value={email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={formError}
                        />
                    </Box>
                    {isSuccess && (
                        <SuccessMessage
                            align="left"
                            message={texts.ENVOYER_UN_LIEN_PAR_EMAIL_POUR_REINITIALISER}
                        />
                    )}
                </TunnelBox>
                <SubmitButton
                    text={texts.REINITIALISER_MOT_DE_PASSE}
                    onClick={handleReset}
                    disabled={buttonLoader}
                    loading={buttonLoader}
                />
            </TunnelStep>
        </Box>
    );
};

export default ForgotPassword;
